import { HttpErrorResponse } from '@angular/common/http';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IBuildingEfficiencyData } from '@app/efficiency-analysis/models/buildings/building-efficiency-data';
import { EABuilding } from '@app/efficiency-analysis/models/buildings/efficiency-analysis-buildings';
import { ComparisonValues } from '@app/efficiency-analysis/models/comparison-values';
import { IRawHeatData } from '@app/efficiency-analysis/models/raw-heat-data';
import { IRawWizardData } from '@app/efficiency-analysis/models/raw-wizard-data';
import { EfficiencyAnalysisDatastoreService } from '@app/efficiency-analysis/services/efficiency-analysis-datastore.service';
import { BuildingsStoreService } from '@app/energy-map/services/buildings-store.service';
import { MeterView } from '@app/shared/models/meter-data/meter-view';
import { NotificationDialogComponent } from '@app/shared/dialog/notification-dialog/notification-dialog.component';
import { ApplicationInsightEvent } from '@app/shared/enums';
import { BuildingTypes } from '@app/shared/models/building-types';
import { ClimateFactorsDto } from '@app/shared/models/efficiency-analysis/climate-factors';
import { EfficiencyAnalysisDto } from '@app/shared/models/efficiency-analysis/efficiency-analysis-dto';
import { EfficiencyAnalysisMeters } from '@app/shared/models/efficiency-analysis/efficiency-analysis-meters';
import { BuildingTypesService } from '@app/shared/services/api/building-types.services';
import { EfficiencyAnalysisApiService } from '@app/shared/services/api/efficiency-analysis-api.service';
import { MeterDataService } from '@app/shared/services/api/meter-data.service';
import { AppInsightsServiceDirective } from '@app/shared/services/app-insights.service';
import { AuthService } from '@app/shared/services/auth.service';
import { EfficiencyAnalysisService } from '@app/shared/services/efficiency-analysis.service';
import { OverlayLoaderService } from '@app/shared/services/overlay-loader.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { cssVariables } from '@env/css-vars';
import { TranslateService } from '@ngx-translate/core';
import { assign, isNil, map as _map, orderBy, toLower } from 'lodash';
import moment from 'moment';
import { Observable, of, Subject, Subscription, throwError } from 'rxjs';
import { catchError, concatMap, first, mergeAll, mergeMap, switchMap, takeUntil, toArray } from 'rxjs/operators';
import { BuildingEfficiencyWizardService } from './building-efficiency-wizard.component.service';
import { BuildingEfficiencyWizardStore } from './building-efficiency-wizard.component.store';
import { ElectricityMeterSelectionService } from './electricity-meter-selection/electricity-meter-selection.service';
import { HeatSelectionService } from './heat-selection/heat-selection.service';
import { DotDayMonthYearFormat, ISODateTimeStartOfDayFormat } from '@app/shared/constants';
import { DateUtilities } from '@app/shared/date-utility';
import { BuildingEfficiencyService } from '@app/shared/services/api/building-efficiency.service';

@Component({
  selector: 'app-building-efficiency-wizard',
  templateUrl: './building-efficiency-wizard.component.html',
  styleUrls: ['./building-efficiency-wizard.component.scss'],
})
export class BuildingEfficiencyWizardComponent implements OnInit, OnDestroy {
  protected unsubscribe$: Subject<void> = new Subject<void>();
  @Input() buildingGuid: string = '';
  @Input() buildingZipcode: string = '';
  @Input() visibility = false;
  @Output() closeEvent = new EventEmitter();
  @Output() saveEmitter = new EventEmitter();

  data?: IRawWizardData;
  numberOfSteps = 4;
  @Input() currentStep = 1;
  @Input() isDisabled = true;
  @Input() viewResults = false;
  heatData: IRawHeatData | null = null;
  building$ = this.buildingStoreService.selectedBuilding$;
  building: any;
  buildingMeters: MeterView[] = [];
  buildingTypes: BuildingTypes[] = [];
  private undefinedObjectType = {
    guid: '',
    name: '',
  } as BuildingTypes;
  public isMeterListLoading = true;
  public efficiency: IBuildingEfficiencyData = {} as IBuildingEfficiencyData;
  private buildingSubscription: Subscription = new Subscription();
  public isSaveDisabled = true;
  public isPeriodChanged = false;
  private startTimeWizard: string | null = null;

  constructor(
    public translate: TranslateService,
    private _buildingEfficiencyWizardStore: BuildingEfficiencyWizardStore,
    private _buildingEfficiencyWizardService: BuildingEfficiencyWizardService,
    private _efficiencyAnalysisApiService: EfficiencyAnalysisApiService,
    private _efficiencyAnalysisService: EfficiencyAnalysisService,
    private _buildingTypesService: BuildingTypesService,
    public overlayLoaderService: OverlayLoaderService,
    public matDialog: MatDialog,
    public meterDataService: MeterDataService,
    public authService: AuthService,
    public buildingStoreService: BuildingsStoreService,
    private efficiencyAnalysisDataStore: EfficiencyAnalysisDatastoreService,
    private electricityMeterSelection: ElectricityMeterSelectionService,
    private heatSelectionService: HeatSelectionService,
    private userMunicipalitiesService: UserMunicipalitiesService,
    private appInsightsServiceDirective: AppInsightsServiceDirective,
    private buildingEfficiencyService: BuildingEfficiencyService
  ) {
    this._buildingEfficiencyWizardStore.clear();
  }

  public ngOnInit(): void {
    this.startTimeWizard = moment().format('YYYY-dd-MM HH:mm:ss');
    this.isSaveDisabled = true;
    this._buildingEfficiencyWizardStore.setIsComputedValuesChanged(false);
    this.overlayLoaderService.isLoading = true;
    this.initializeBuildingEfficiencyWizard();

    this._efficiencyAnalysisApiService
      .getClimateFactor(this.buildingZipcode)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((factors: ClimateFactorsDto[]) => (this._efficiencyAnalysisService.climateFactors = factors));

    this._buildingEfficiencyWizardStore.efficiencyAnalysis$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((analysis: EfficiencyAnalysisDto) => (this.efficiency = analysis as unknown as IBuildingEfficiencyData));

    this.buildingSubscription = this.building$.subscribe((building) => {
      const buildingEA = building?.efficiencyAnalyses;
      const electricityColorStatusDefined = !!buildingEA[0]?.electricityColorStatus;
      const heatColorStatusDefined = !!buildingEA[0]?.heatColorStatus;
      const hasValidEA = electricityColorStatusDefined && heatColorStatusDefined;
      if (hasValidEA) {
        this.currentStep = this.numberOfSteps;
      }
    });

    this._buildingEfficiencyWizardStore.isComputedValuesChanged$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isChanged: boolean) => (this.isSaveDisabled = !isChanged));
  }

  public ngOnDestroy(): void {
    this._buildingEfficiencyWizardStore.clear();
    this._buildingEfficiencyWizardStore.heatSelections = [];
    this.buildingSubscription.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private initializeBuildingEfficiencyWizard(): void {
    const municipalityKey = this.userMunicipalitiesService.currentMunicipality.key;
    this.buildingEfficiencyService
      .getBuildingEffieciencyByBuildingId(this.buildingGuid, municipalityKey)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.logEAErrorEvent(error, 'getBuildingEffieciencyByBuildingId');
          return throwError(() => error);
        }),
        mergeAll(),
        concatMap((efficiencyData: IBuildingEfficiencyData) => this.handleEfficiencyData(efficiencyData)),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((heatData: IRawHeatData | null) => (this.heatData = heatData));
  }

  private handleEfficiencyData(efficiencyData: IBuildingEfficiencyData): Observable<IRawHeatData | null> {
    this._buildingEfficiencyWizardStore.setBuilding(efficiencyData as EABuilding);

    return this._buildingTypesService.getById(efficiencyData.buildingTypeId).pipe(
      catchError((error: HttpErrorResponse) => {
        this.logEAErrorEvent(error, '_buildingTypesService.getById');
        return of([]);
      }),
      concatMap((buildingType: BuildingTypes[]) => this.handleBuildingTypeData(buildingType, efficiencyData))
    );
  }

  private handleBuildingTypeData(buildingType: BuildingTypes[], efficiencyData: IBuildingEfficiencyData): Observable<IRawHeatData | null> {
    const buildingTypeData = buildingType;
    efficiencyData.buildingTypeName = buildingTypeData[0].name;

    return of(efficiencyData).pipe(concatMap((buildingEfficiency: IBuildingEfficiencyData) => this.handleEfficiencyAnalysisData(buildingEfficiency)));
  }

  private handleEfficiencyAnalysisData(buildingEfficiency: IBuildingEfficiencyData): Observable<IRawHeatData | null> {
    const hasTimePeriod = this._buildingEfficiencyWizardService.hasTimePeriod(buildingEfficiency);

    const newWizardData: IRawWizardData = {
      defaultStartDate: moment().subtract(2, 'years').startOf('year').format(ISODateTimeStartOfDayFormat),
      defaultEndDate: moment().subtract(2, 'years').endOf('year').format(ISODateTimeStartOfDayFormat),
      userDefinedStartDate: hasTimePeriod ? String(buildingEfficiency.efficiencyAnalysisStartDate) : null,
      userDefinedEndDate: hasTimePeriod ? String(buildingEfficiency.efficiencyAnalysisEndDate) : null,
      hasTimePeriod: hasTimePeriod,
      buildingEfficiencyData: buildingEfficiency,
      isElectricityValueSuggested: buildingEfficiency.isElectricityValueSuggested,
      isHeatValueSuggested: buildingEfficiency.isHeatValueSuggested,
    } as IRawWizardData;

    return this.handleComparisonValues(newWizardData);
  }

  private handleComparisonValues(newWizardData: IRawWizardData): Observable<IRawHeatData | null> {
    return this.efficiencyAnalysisDataStore.comparisonValues$.pipe(
      concatMap((comparisonValues: ComparisonValues[]) => this.setComparisonValuesInBuildingEfficiencyWizardService(comparisonValues, newWizardData))
    );
  }

  private setComparisonValuesInBuildingEfficiencyWizardService(
    comparisonValues: ComparisonValues[],
    newWizardData: IRawWizardData
  ): Observable<IRawHeatData | null> {
    this._buildingEfficiencyWizardService.comparisonValues = comparisonValues.find(
      (compValues: ComparisonValues) => compValues.buildingTypeId === newWizardData.buildingEfficiencyData.buildingTypeId
    ) as ComparisonValues;

    return this.efficiencyAnalysisDataStore.buildingTypes$.pipe(
      concatMap((buildingTypes: BuildingTypes[]) => this.processBuildingTypes(buildingTypes, newWizardData))
    );
  }

  private processBuildingTypes(buildingTypes: BuildingTypes[], newWizardData: IRawWizardData): Observable<IRawHeatData | null> {
    this.buildingTypes = orderBy(buildingTypes, (types: BuildingTypes) => types.name, ['asc']);
    this.buildingTypes.splice(0, 0, this.undefinedObjectType);

    this.buildingMeters = [];
    this.data = newWizardData;
    this.getMeterList(newWizardData);

    return this._buildingEfficiencyWizardStore.heatData$;
  }

  private logEAErrorEvent(error: HttpErrorResponse, errorArea: string): void {
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.errorEfficiencyAnalysis, {
      objectType: this.data?.buildingEfficiencyData?.buildingTypeName,
      currentStep: this.currentStep,
      error: error,
      errorArea: errorArea,
    });
  }

  getMeterList(newWizardData: IRawWizardData) {
    this.isMeterListLoading = true;
    const municipalityKey = newWizardData.buildingEfficiencyData.municipalityKey;
    const connectionObjectNumber = newWizardData.buildingEfficiencyData.connectionObjectNumber;

    this.meterDataService
      .getMetersByConnectionNumber(municipalityKey, [connectionObjectNumber])
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.isMeterListLoading = false;
          this.overlayLoaderService.isLoading = false;
          this.logEAErrorEvent(error, 'getMetersByConnectionNumber');
          return of([]);
        }),
        takeUntil(this.unsubscribe$)
      )
      .subscribe((res: MeterView[]) => {
        if (!isNil(res)) {
          this.buildingMeters = _map(res, (item) => {
            const mv = assign(new MeterView(), item);

            mv.objectTypeObj =
              this.buildingTypes.find((bt: BuildingTypes) => toLower(bt.name).trim() === toLower(mv.objectType).trim()) || this.undefinedObjectType;

            mv.nummers = [];
            if (mv.getMeterNumberWithOutLeadingZeros()) {
              mv.nummers.push(`${mv.getMeterNumberWithOutLeadingZeros()}`);
            }
            if (mv.getMarketLocationKeyWithOutLeadingZeros()) {
              mv.nummers.push(`${mv.getMarketLocationKeyWithOutLeadingZeros()}`);
            }
            if (this.authService.permissions.SENSOR_DATA && mv.deviceEUI) {
              mv.nummers.push(`${mv.getSensorNumberWithOutLeadingZeros()}`);
            }

            return mv;
          });

          const unique: any[] = [];
          this.buildingMeters.forEach((x) => (unique.filter((a) => a.guid === x.guid).length > 0 ? null : unique.push(x)));
          this.buildingMeters = unique;
          this.sortMeterList();

          this.data = newWizardData;
          newWizardData.meterList = this.buildingMeters;
          this._buildingEfficiencyWizardStore.update(newWizardData);
          this._buildingEfficiencyWizardStore.updateMeters(this.buildingMeters);
        }
        this.isMeterListLoading = false;
        this.overlayLoaderService.isLoading = false;
      });
  }

  sortMeterList() {
    const buildingMeters = this.buildingMeters.filter((a) => toLower(a.buildingTypesName).trim() === toLower(a.objectType).trim());
    const otherMeters = orderBy(
      this.buildingMeters.filter((a) => toLower(a.buildingTypesName).trim() !== toLower(a.objectType).trim()),
      ['objectType'],
      ['asc']
    );
    this.buildingMeters = [...buildingMeters, ...otherMeters];
  }

  getNextButtonState(status: boolean) {
    this.isDisabled = status;
  }

  closeWizard() {
    if ((this.currentStep === this.numberOfSteps && this.viewResults) || this.isSaveDisabled) {
      this.closeResultsPage();
    } else {
      const matRef = this.matDialog.open(NotificationDialogComponent, {
        data: {
          descriptionText: 'Ein Entwurf dieser Effizienzanalyse wurde automatisch gespeichert. Möchten Sie diesen beibehalten?',
          orientation: 'column',
          confirmText: 'Ja',
          declineText: 'Nein',
          hideClose: true,
        },
        panelClass: ['-md'],
        disableClose: true,
      });
      matRef.afterClosed().subscribe((confirm) => {
        if (!confirm) {
          this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.cancelEfficiencyAnalysis, {
            objectType: this.data?.buildingEfficiencyData?.buildingTypeName,
            currentStep: this.currentStep,
          });

          this._buildingEfficiencyWizardStore.heatSelections = [];
          this.closeResultsPage();
        } else {
          this.save();
        }
      });
    }
  }

  next() {
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.wizardNextEventEfficiencyAnalysis, {
      objectType: this.data?.buildingEfficiencyData?.buildingTypeName,
      currentStep: this.currentStep,
      data: this.data,
    });

    if (this.currentStep === this.numberOfSteps) {
      this.closeEvent.emit(true);
    } else if (this.currentStep >= 0) {
      switch (this.currentStep) {
        case 1:
          if (this.isPeriodChanged) {
            this.electricityMeterSelection.setIsConsumptionAutoSuggested(true);
            this.electricityMeterSelection.hasSuggestedConsumptionsStore.next(false);
            this._buildingEfficiencyWizardService.isPeriodChanged = true;
            this.isPeriodChanged = false;
          }
          this.currentStep++;
          break;
        case 2:
          this.currentStep++;
          break;
        case 3:
          const meters: any[] = [];
          this.data?.meterList?.forEach((meter: MeterView) => {
            if (!isNil(meter.consumption)) {
              meter.consumption = this.unlocalize(meter.consumption);
            }
            meters.push(meter);
          });
          if (this.data) {
            this.data.meterList = meters;
            this._buildingEfficiencyWizardService.calculateEfficiency(this.data, this.heatData as IRawHeatData);
            const calculatedValues = this._buildingEfficiencyWizardService.calculatedEfficiency;
            if (this.data.buildingEfficiencyData) {
              this.data.buildingEfficiencyData.efficiencyAnalysisStartDate = this.data.hasTimePeriod
                ? moment(this.data.userDefinedStartDate).toDate()
                : moment(this.data.defaultStartDate).toDate();
              this.data.buildingEfficiencyData.efficiencyAnalysisEndDate = this.data.hasTimePeriod
                ? moment(this.data.userDefinedEndDate).toDate()
                : moment(this.data.defaultEndDate).toDate();
              if (calculatedValues) {
                this.data.buildingEfficiencyData.electricityComputedValue = calculatedValues.electricity.consumption;
                this.data.buildingEfficiencyData.electricityColorStatus = calculatedValues.electricity.colorStatus;
                this.data.buildingEfficiencyData.averageElectricityConsumption = calculatedValues.electricity.average;
                this.data.buildingEfficiencyData.electricityGuideValue = calculatedValues.electricity.guideValue;
                this.data.buildingEfficiencyData.electricityMinDataRange = calculatedValues.electricity.minRange;
                this.data.buildingEfficiencyData.electricityMaxDataRange = calculatedValues.electricity.maxRange;
                this.data.buildingEfficiencyData.efficiencyPercentageElectricity = calculatedValues.electricity.percentage;
                this.data.buildingEfficiencyData.heatComputedValue = calculatedValues.heat.consumption;
                this.data.buildingEfficiencyData.heatColorStatus = calculatedValues.heat.colorStatus;
                this.data.buildingEfficiencyData.averageHeatConsumption = calculatedValues.heat.average;
                this.data.buildingEfficiencyData.heatGuideValue = calculatedValues.heat.guideValue;
                this.data.buildingEfficiencyData.heatMinDataRange = calculatedValues.heat.minRange;
                this.data.buildingEfficiencyData.heatMaxDataRange = calculatedValues.heat.maxRange;
                this.data.buildingEfficiencyData.efficiencyPercentageHeat = calculatedValues.heat.percentage;
              }

              this.data.buildingEfficiencyData.efficiencyAnalysisDate = moment().toDate();
            }

            this._buildingEfficiencyWizardStore.update(this.data);
          }

          this.currentStep++;
          break;
        case 4:
          this.currentStep++;
          break;
      }
    }
  }

  previous() {
    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.wizardPreviousEventEfficiencyAnalysis, {
      objectType: this.data?.buildingEfficiencyData?.buildingTypeName,
      currentStep: this.currentStep,
      data: this.data,
    });

    switch (this.currentStep) {
      case 1:
        this.closeWizard();
        break;
      case 2:
        this.currentStep--;
        break;
      case 3:
        this.currentStep--;
        break;
      case 4:
        this.currentStep--;
        break;
    }
  }

  public save(): void {
    this.overlayLoaderService.isLoading = true;
    const municipalityKey = this.userMunicipalitiesService.currentMunicipality.key;

    this._buildingEfficiencyWizardStore
      .saveEfficiencyAnalysis(this.mapSaveData(this.data, this.efficiency.guid), municipalityKey)
      .pipe(
        switchMap((efficiencyAnalysis: EfficiencyAnalysisDto) => {
          return this.mapSelectionsToEfficiencyMeters(
            this.heatSelectionService.mapHeatSelectionsToEfficiencyMeters(this._buildingEfficiencyWizardStore.heatSelections, efficiencyAnalysis.guid),
            this.electricityMeterSelection.mapMetersToEfficiencyMeters(efficiencyAnalysis.guid)
          );
        }),
        mergeMap((metersToEfficiencyMeters: Partial<EfficiencyAnalysisMeters>) =>
          this._buildingEfficiencyWizardStore.saveEfficiencyAnalysisMeter(metersToEfficiencyMeters, municipalityKey)
        ),
        toArray(),
        switchMap(() => this.efficiencyAnalysisDataStore.loadBuildingEfficiencyAnalysis()),
        catchError((error: HttpErrorResponse) => {
          this.logEAErrorEvent(error, 'saveEfficiencyAnalysis Error');
          this.showErrorModal('A general exception has occured');
          this.closeEvent.emit(false);
          return throwError(() => error);
        }),
        first()
      )
      .subscribe(() => this.afterEASave());
  }

  private afterEASave(): void {
    this.electricityMeterSelection.hasSuggestedConsumptionsStore.next(false);
    this.electricityMeterSelection.keyPressed = false;
    this.electricityMeterSelection.efficiencyMetersStore.next([]);
    this.electricityMeterSelection.metersStore.next([]);
    this._buildingEfficiencyWizardService.isPeriodChanged = false;
    this.heatSelectionService.efficiencyMeters = [];

    this.appInsightsServiceDirective.logEvent(ApplicationInsightEvent.saveEfficiencyAnalysis, {
      objectType: this.data?.buildingEfficiencyData?.buildingTypeName,
      currentStep: this.currentStep,
      data: this.data,
      warmeStatus: this.data?.buildingEfficiencyData?.heatColorStatus,
      stromStatus: this.data?.buildingEfficiencyData?.electricityColorStatus,
      startTimeWizard: this.startTimeWizard,
      saveTimeWizard: moment().format('YYYY-dd-MM HH:mm:ss'),
    });

    this.saveEmitter.emit(this.data);
    this.closeEvent.emit(false);
    this.visibility = false;
    this.overlayLoaderService.isLoading = false;
  }

  private mapSelectionsToEfficiencyMeters(
    heatSelections: Partial<EfficiencyAnalysisMeters>[],
    electricitySelections: Partial<EfficiencyAnalysisMeters>[]
  ): Partial<EfficiencyAnalysisMeters>[] {
    return (heatSelections || []).concat(electricitySelections || []);
  }

  private mapSaveData(data: any, guid: string | null = null): Partial<EfficiencyAnalysisDto> {
    const saveData: Partial<EfficiencyAnalysisDto> = {
      buildingId: data.buildingEfficiencyData.guid,
      floorArea: data.buildingEfficiencyData.floorArea,
      electricityConsumption: data.buildingEfficiencyData.electricityConsumption,
      averageElectricityConsumption: data.buildingEfficiencyData.averageElectricityConsumption,
      electricityGuideValue: data.buildingEfficiencyData.electricityGuideValue,
      electricityMinDataRange: data.buildingEfficiencyData.electricityMinDataRange,
      electricityMaxDataRange: data.buildingEfficiencyData.electricityMaxDataRange,
      heatingOilConsumption: data.buildingEfficiencyData.heatingOilConsumption,
      naturalGasConsumption: data.buildingEfficiencyData.naturalGasConsumption,
      lpgConsumption: data.buildingEfficiencyData.lpgConsumption,
      districtHeatingConsumption: data.buildingEfficiencyData.districtHeatingConsumption,
      electricityHeatConsumption: data.buildingEfficiencyData.electricityHeatConsumption,
      pelletWoodConsumption: data.buildingEfficiencyData.pelletWoodConsumption,
      averageHeatConsumption: data.buildingEfficiencyData.averageHeatConsumption,
      heatGuideValue: data.buildingEfficiencyData.heatGuideValue,
      heatMinDataRange: data.buildingEfficiencyData.heatMinDataRange,
      heatMaxDataRange: data.buildingEfficiencyData.heatMaxDataRange,
      electricityColorStatus: data.buildingEfficiencyData.electricityColorStatus,
      heatColorStatus: data.buildingEfficiencyData.heatColorStatus,
      electricityComputedValue: data.buildingEfficiencyData.electricityComputedValue,
      heatComputedValue: data.buildingEfficiencyData.heatComputedValue,
      efficiencyAnalysisDate: data.buildingEfficiencyData.efficiencyAnalysisDate,
      efficiencyPercentageElectricity: data.buildingEfficiencyData.efficiencyPercentageElectricity,
      efficiencyPercentageHeat: data.buildingEfficiencyData.efficiencyPercentageHeat,
      efficiencyAnalysisStartDate: data.userDefinedStartDate ?? data.buildingEfficiencyData.efficiencyAnalysisStartDate,
      efficiencyAnalysisEndDate: data.userDefinedEndDate ?? data.buildingEfficiencyData.efficiencyAnalysisEndDate,
      isElectricityValueSuggested: this.electricityMeterSelection.hasSuggestedConsumptionsStore.value
        ? this.electricityMeterSelection.isConsumptionAutoSuggestedStore.value
          ? this.electricityMeterSelection.isConsumptionAutoSuggestedStore.value
          : !isNil(this.efficiency.efficiencyAnalysisDate) && isNil(this.efficiency.isElectricityValueSuggested)
          ? !this.areDatesSimilar(this.electricityMeterSelection.startDate, this.efficiency.efficiencyAnalysisStartDate)
            ? false
            : undefined
          : false
        : undefined,
      isHeatValueSuggested: this.heatSelectionService.toggledSubject$.value ? this.heatSelectionService.toggledSubject$.value : undefined,
      notes: data.buildingEfficiencyData.notes,
      creationDate: moment.utc().toDate(),
      modifyDate: moment.utc().toDate(),
      modifyUser: this.authService.currentUser.guid,
    };

    if (!isNil(guid)) {
      saveData.guid = guid;
    }

    return saveData;
  }

  closeResultsPage() {
    this.electricityMeterSelection.hasSuggestedConsumptionsStore.next(false);
    this.electricityMeterSelection.keyPressed = false;
    this.electricityMeterSelection.efficiencyMetersStore.next([]);
    this.electricityMeterSelection.metersStore.next([]);
    this._buildingEfficiencyWizardService.isPeriodChanged = false;
    this.heatSelectionService.efficiencyMeters = [];
    this.visibility = false;
    this.buildingSubscription.unsubscribe();
    this.closeEvent.emit(false);
  }

  private showErrorModal(message: string): void {
    this.matDialog.open(NotificationDialogComponent, {
      data: {
        descriptionText: message,
        descriptionFaClass: 'fa-exclamation-triangle fa-2x',
        descriptionFaColor: cssVariables['--ep-error-color'],
      },
      position: { top: '40px' },
      panelClass: ['-md'],
    });
  }

  unlocalize(value: any) {
    return parseInt(value.toString().replace(',0', '').split(',').join('').split('.').join(''), 10);
  }

  private areDatesSimilar(date1: string | Date, date2: string | Date): boolean {
    return DateUtilities.toExpectedFormat(date1, DotDayMonthYearFormat) === DateUtilities.toExpectedFormat(date2, DotDayMonthYearFormat);
  }
}
